import { isNil } from 'lodash';
import { CSSProperties, useCallback } from 'react';
import { Modal } from '@shared/components/Modal';
import { ScrollView, View } from '@shared/components/View';
import { AuditCheckboxField } from '@shared/data/entity/fields/AuditCheckboxField';
import { AuditTagField } from '@shared/data/entity/fields/AuditTagField';
import { AuditTextField } from '@shared/data/entity/fields/AuditTextField';
import { castField } from '@shared/data/entity/fields/Field';
import { useAiSummariesEnabled } from '@shared/scenes/ask/useAiSummariesEnabled';
import { EditField } from '@shared/scenes/entity/components/fields/EditField';
import {
  SubformLayoutEntry,
  isSubformComponentEntry,
} from '@shared/scenes/entity/components/fields/Subform/subformLayoutEntries';
import { SubformData } from '@shared/scenes/entity/components/fields/Subform/useSubformData';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { FieldRecord, UpdateField } from '@shared/scenes/entity/types';
import { getFieldError } from '@shared/scenes/entity/util/getFieldError';
import { hasLayoutContent } from '@shared/scenes/workflows/viewTemplateLayoutItems';
import { grid } from '@shared/styles';
import { SupportedSubformComponentValue } from '@shared/types/fieldValues';
import { isStaticTextComponent } from '../../utils';
import { SubformSummary } from './SubformSummary';
import { LayoutImageItem, LayoutTextItem, LayoutVideoItem } from './layoutItems';
import styles from './SubformView.module.css';

interface Props extends SubformData {
  onSubmit: (params: {
    data: Record<string, SupportedSubformComponentValue | null>;
    wasValid: boolean;
  }) => void;
  isAuthenticated?: boolean;
  parentWorkflowTemplateId?: string;
  isReadOnly?: boolean;
  /** The card (workflow-entity) id, used to scope the "Summarize" widget's Ask request.
   *  When absent (e.g. unsaved/bulk/external forms) the widget is not shown. */
  entityId?: string;
  /** A previously generated summary for this subform, if any. Persisted above the view. */
  summary?: string;
  /** Stores a newly generated summary in the card-scoped state above this view. */
  onSummaryGenerated?: (summary: string) => void;
}

export function SubformView({
  workflowTemplate,
  components,
  layoutEntries,
  orderedComponents,
  fields,
  updateField,
  errors,
  isValid,
  isInitialized,
  setSubmitAttempted,
  onSubmit,
  isAuthenticated = true,
  parentWorkflowTemplateId,
  isReadOnly = false,
  entityId,
  summary,
  onSummaryGenerated,
}: Props) {
  const areSummariesEnabled = useAiSummariesEnabled();

  const handleBack = useCallback(() => {
    if (!isReadOnly) {
      setSubmitAttempted(true);
    }
    const payload: Record<string, SupportedSubformComponentValue | null> = {};
    for (const component of components) {
      if (isStaticTextComponent(component)) {
        continue;
      }
      const field = fields[component.id];

      const auditField =
        castField(field, AuditTextField) ??
        castField(field, AuditTagField) ??
        castField(field, AuditCheckboxField);
      const shouldPersist = !isNil(auditField)
        ? auditField.hasData
        : !isNil(field) && !field.isEmpty;
      payload[component.id] = shouldPersist && !isNil(field) ? field.toFieldJson() : null;
    }
    onSubmit({ data: payload, wasValid: isReadOnly || isValid });
  }, [isValid, isReadOnly, components, fields, onSubmit, setSubmitAttempted]);

  return (
    <Modal
      isOpen
      fullPage
      onOpenChange={handleBack}
      className={styles.subformModal}
      testID="subform-modal"
    >
      <Modal.TitleBar
        title={workflowTemplate?.name}
        startContent={<Modal.BackButton onClick={handleBack} />}
      />
      {/*
       * The gray background is on the ScrollView itself (not an inner wrapper)
       * so it fills the full scroll area and never scrolls away with content.
       * The content wrapper's flexGrow:1 ensures it fills the visible modal
       * height even when there are few fields.
       */}
      <ScrollView style={scrollContainerStyle}>
        <View style={scrollContentStyle}>
          <View style={fieldContainerStyle}>
            {areSummariesEnabled &&
              isAuthenticated &&
              isInitialized &&
              !isNil(entityId) &&
              !isNil(onSummaryGenerated) && (
                <SubformSummary
                  entityId={entityId}
                  workflowTemplate={workflowTemplate}
                  orderedComponents={orderedComponents}
                  fields={fields}
                  summary={summary}
                  onSummaryGenerated={onSummaryGenerated}
                />
              )}
            {isInitialized &&
              layoutEntries.map(entry => (
                <LayoutEntry
                  key={entry.key}
                  entry={entry}
                  fields={fields}
                  updateField={updateField}
                  errors={errors}
                  isAuthenticated={isAuthenticated}
                  workflowTemplateId={
                    parentWorkflowTemplateId ??
                    workflowTemplate?.parentId ??
                    workflowTemplate?.id ??
                    ''
                  }
                />
              ))}
          </View>
        </View>
      </ScrollView>
    </Modal>
  );
}

interface LayoutEntryProps {
  entry: SubformLayoutEntry;
  fields: FieldRecord;
  updateField: UpdateField;
  errors: CardEntityError[];
  isAuthenticated: boolean;
  workflowTemplateId: string;
}

/**
 * One slot of the subform's view template layout: a field to fill in, or a text, image or video
 * item to read.
 *
 * The content items are indented to the same gutter as the fields rather than running to the
 * modal's edge, so a form of both reads as one column. They take no part in the payload — see
 * `handleBack`, which walks the components — because there is nothing to fill in.
 *
 * An item holding nothing yet renders nothing at all, wrapper included: an empty gutter div still
 * carries the spacing below it and would open a gap in the column.
 */
function LayoutEntry({
  entry,
  fields,
  updateField,
  errors,
  isAuthenticated,
  workflowTemplateId,
}: LayoutEntryProps) {
  if (!isSubformComponentEntry(entry)) {
    if (!hasLayoutContent(entry.item)) {
      return null;
    }

    return (
      <View className={styles.contentItem}>
        {entry.kind === 'text' && <LayoutTextItem item={entry.item} />}
        {entry.kind === 'image' && <LayoutImageItem item={entry.item} />}
        {entry.kind === 'video' && <LayoutVideoItem item={entry.item} />}
      </View>
    );
  }

  const { component } = entry;

  return (
    <EditField
      component={component}
      field={fields[component.id]}
      fields={fields}
      updateField={updateField}
      error={getFieldError(errors, component.id)}
      isAuthenticated={isAuthenticated}
      workflowTemplateId={workflowTemplateId}
      style={fieldStyle}
    />
  );
}

const scrollContainerStyle = {
  flex: 1,
  paddingTop: grid(1),
  paddingBottom: grid(4),
};

const scrollContentStyle = {
  flexGrow: 1,
  flexShrink: 0,
  paddingTop: grid(1),
  paddingBottom: grid(4),
  paddingLeft: grid(1),
  paddingRight: grid(1),
};

const fieldContainerStyle = {
  width: '100%',
  maxWidth: 456,
  alignSelf: 'center' as const,
};

// Matches EntityFormResponsive's formComponent + formComponentStandalone spacing.
const fieldStyle: CSSProperties = {
  paddingTop: grid(0.25),
  paddingBottom: grid(1.75),
  marginLeft: grid(2),
  marginRight: grid(2),
};
